import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { api } from "../api/axios";
import { PageHeader, Pill, StatTile } from "../components/ui";

export default function AdminInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const theme = useTheme();

  useEffect(() => {
    api
      .get("/api/admin/insights")
      .then(({ data }) => {
        setData(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "שגיאה בטעינת נתונים");
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const COLORS = [theme.palette.primary.main, "#0f766e", "#7c3aed", theme.palette.warning.main, theme.palette.success.main];
  const { counts, userGrowth, siteGrowth, revenueGrowth, planDistribution, siteUsage, expiringUsers } = data;

  const cardStyle = { height: "100%" };
  const gridStroke = theme.aw.border;
  const tooltipStyle = { backgroundColor: theme.palette.background.paper, borderRadius: 12, border: `1px solid ${theme.aw.border}`, boxShadow: theme.aw.shadowMd };

  const getUsageStatus = (hits, limit) => {
    if (!limit) return { label: "Free / Unlimited", color: "info" };
    const pct = (hits / limit) * 100;
    if (pct >= 100) return { label: "Exceeded", color: "error" };
    if (pct >= 80) return { label: "Critical", color: "warning" };
    return { label: "OK", color: "success" };
  };

  return (
    <Box>
      <PageHeader title="Insights & Statistics" subtitle="מדדי צמיחה, הכנסות ושימוש בכל המערכת" />

      {/* Summary Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {expiringUsers?.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>מנויים שמסתיימים בקרוב (14 יום)</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {expiringUsers.map(u => (
                  <Chip 
                    key={u.id}
                    label={`${u.email} (${new Date(u.ends_at).toLocaleDateString('he-IL')})`}
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <StatTile sx={cardStyle} icon={<PeopleAltOutlinedIcon />} tone="primary" label="Total Users" value={counts.totalUsers} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile sx={cardStyle} icon={<LanguageOutlinedIcon />} tone="teal" label="Total Sites" value={counts.totalSites} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile sx={cardStyle} icon={<WorkspacePremiumOutlinedIcon />} tone="violet" label="Active Subscriptions" value={counts.activeSubscriptions} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile sx={cardStyle} icon={<PaymentsOutlinedIcon />} tone="success" label="Total Revenue" value={`₪${Number(counts.totalRevenue || 0).toLocaleString()}`} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* User Growth Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ ...cardStyle, p: 3 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>משתמשים חדשים לאורך זמן</Typography>
            <Box sx={{ height: 350, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={userGrowth}>
                  <defs>
                    <linearGradient id="colorUser" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.9}/>
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.35}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} fontSize={12} tickFormatter={(val) => new Date(val).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })} />
                  <YAxis stroke={theme.palette.text.secondary} fontSize={12} />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelFormatter={(val) => new Date(val).toLocaleDateString('he-IL')}
                  />
                  <Bar dataKey="count" name="משתמשים" fill="url(#colorUser)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Plan Distribution Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card sx={{ ...cardStyle, p: 3 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>התפלגות חבילות</Typography>
            <Box sx={{ height: 350, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={planDistribution}
                    innerRadius={80}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="name"
                  >
                    {planDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Site Growth Line Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ ...cardStyle, p: 3 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>אתרים חדשים (30 יום)</Typography>
            <Box sx={{ height: 300, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={siteGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} fontSize={12} tickFormatter={(val) => new Date(val).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })} />
                  <YAxis stroke={theme.palette.text.secondary} fontSize={12} />
                  <Tooltip labelFormatter={(val) => new Date(val).toLocaleDateString('he-IL')} />
                  <Line type="monotone" dataKey="count" name="אתרים" stroke={theme.palette.success.main} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Revenue Growth Line Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ ...cardStyle, p: 3 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>הכנסות יומיות (30 יום)</Typography>
            <Box sx={{ height: 300, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueGrowth}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.warning.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.warning.main} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} fontSize={12} tickFormatter={(val) => new Date(val).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })} />
                  <YAxis stroke={theme.palette.text.secondary} fontSize={12} />
                  <Tooltip labelFormatter={(val) => new Date(val).toLocaleDateString('he-IL')} />
                  <Area type="monotone" dataKey="amount" name="הכנסה" stroke={theme.palette.warning.main} fillOpacity={1} fill="url(#colorRev)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Site Usage Report */}
      <Typography variant="h5" component="h2" sx={{ mt: 5, mb: 2 }}>
        דוח שימוש ותעבורה גלובלי
      </Typography>
      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>אתר / דומיין</TableCell>
                <TableCell>משתמש</TableCell>
                <TableCell>טעינות (החודש)</TableCell>
                <TableCell>מגבלה</TableCell>
                <TableCell>ניצול</TableCell>
                <TableCell>סטטוס</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {siteUsage?.map((row) => {
                const status = getUsageStatus(row.hits, row.monthly_limit);
                const pct = row.monthly_limit ? Math.min(100, (row.hits / row.monthly_limit) * 100) : 0;
                
                return (
                  <TableRow key={row.site_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.domain}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.6 }}>#{row.site_id} · {row.site_status}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.user_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {Number(row.hits).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.monthly_limit ? Number(row.monthly_limit).toLocaleString() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={pct} 
                            color={status.color}
                            aria-label={`${Math.round(pct)}%`}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 35, fontWeight: 700 }}>
                          {Math.round(pct)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Pill tone={status.color}>{status.label}</Pill>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!siteUsage || siteUsage.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    אין נתוני שימוש להצגה
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
